import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArticleRoleAssignment,
  ExpenseArticleAccessItem,
  Role,
  getArticleRolesAccess,
  saveArticleRolesAccess,
} from '../services/api';
import { SortState, compareValues, toggleSort } from '../utils/table';
import { useAuth } from '../context/AuthContext';
import { SheetsColumnMultiFilter } from '../components/SheetsColumnMultiFilter';
import '../styles/DataPage.css';
import './ArticlesPage.css';
import './ArticleRolesAccessPage.css';

type ArticleAccessSortKey = 'planfact_article' | 'category' | 'section';
type ArticleAccessFilterMenuKey = 'planfact_article' | 'category' | 'section' | 'role' | null;

function displayValue(value: string | null | undefined): string {
  const trimmed = value?.trim();
  return trimmed ? trimmed : '—';
}

function uniqueSorted(values: Array<string | null | undefined>): string[] {
  const set = new Set<string>();
  for (const value of values) {
    set.add(displayValue(value));
  }
  return [...set].sort((a, b) => a.localeCompare(b, 'ru'));
}

function assignmentKey(articleId: number, roleId: number): string {
  return `${articleId}:${roleId}`;
}

function draftFromAssignments(assignments: ArticleRoleAssignment[]): Record<string, boolean> {
  const next: Record<string, boolean> = {};
  for (const item of assignments) {
    next[assignmentKey(item.article_id, item.role_id)] = true;
  }
  return next;
}

function serializeChecked(draft: Record<string, boolean>): string {
  return Object.entries(draft)
    .filter(([, checked]) => checked)
    .map(([key]) => key)
    .sort()
    .join('|');
}

function matchesSearch(article: ExpenseArticleAccessItem, query: string): boolean {
  if (!query.trim()) return true;
  const haystack = [article.planfact_article, article.category, article.section]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(query.trim().toLowerCase());
}

function matchesScalarFilter(selected: string[], value: string | null | undefined): boolean {
  if (selected.length === 0) return true;
  return selected.includes(displayValue(value));
}

const ArticleRolesAccessPage: React.FC = () => {
  const { can } = useAuth();
  const canView = can('ACCESS_ADMIN', 'VIEW');
  const canEdit = can('ACCESS_ADMIN', 'EDIT');

  const [articles, setArticles] = useState<ExpenseArticleAccessItem[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [draft, setDraft] = useState<Record<string, boolean>>({});
  const [savedDraft, setSavedDraft] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [searchText, setSearchText] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterMenuOpen, setFilterMenuOpen] = useState<ArticleAccessFilterMenuKey>(null);
  const [filterPlanfactArticles, setFilterPlanfactArticles] = useState<string[]>([]);
  const [filterCategories, setFilterCategories] = useState<string[]>([]);
  const [filterSections, setFilterSections] = useState<string[]>([]);
  const [filterRoleNames, setFilterRoleNames] = useState<string[]>([]);
  const [sortState, setSortState] = useState<SortState<ArticleAccessSortKey>>({
    key: 'planfact_article',
    direction: 'asc',
  });

  const applyPayload = useCallback((payload: {
    articles: ExpenseArticleAccessItem[];
    roles: Role[];
    assignments: ArticleRoleAssignment[];
  }): void => {
    const nextDraft = draftFromAssignments(payload.assignments);
    setArticles(payload.articles);
    setRoles(payload.roles);
    setDraft(nextDraft);
    setSavedDraft(nextDraft);
  }, []);

  const load = useCallback(async (): Promise<void> => {
    if (!canView) {
      setLoading(false);
      setArticles([]);
      setRoles([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      applyPayload(await getArticleRolesAccess());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка загрузки');
      setArticles([]);
      setRoles([]);
    } finally {
      setLoading(false);
    }
  }, [applyPayload, canView]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!filtersOpen) {
      setFilterMenuOpen(null);
    }
  }, [filtersOpen]);

  const planfactArticleFilterOptions = useMemo(
    () => uniqueSorted(articles.map((row) => row.planfact_article)),
    [articles],
  );

  const categoryFilterOptions = useMemo(
    () => uniqueSorted(articles.map((row) => row.category)),
    [articles],
  );

  const sectionFilterOptions = useMemo(
    () => uniqueSorted(articles.map((row) => row.section)),
    [articles],
  );

  const roleFilterOptions = useMemo(
    () => [...roles].map((role) => role.name).sort((a, b) => a.localeCompare(b, 'ru')),
    [roles],
  );

  const clearFilters = (): void => {
    setFilterPlanfactArticles([]);
    setFilterCategories([]);
    setFilterSections([]);
    setFilterRoleNames([]);
    setFilterMenuOpen(null);
  };

  const hasActiveFilters =
    filterPlanfactArticles.length > 0 ||
    filterCategories.length > 0 ||
    filterSections.length > 0 ||
    filterRoleNames.length > 0;

  const isDirty = serializeChecked(draft) !== serializeChecked(savedDraft);

  const filteredArticles = useMemo(() => {
    const selectedRoleIds = new Set(
      roles.filter((role) => filterRoleNames.includes(role.name)).map((role) => role.id),
    );
    return articles.filter((row) => {
      const matchesRoles =
        selectedRoleIds.size === 0 ||
        [...selectedRoleIds].some((roleId) => Boolean(draft[assignmentKey(row.id, roleId)]));
      return (
        matchesSearch(row, searchText) &&
        matchesScalarFilter(filterPlanfactArticles, row.planfact_article) &&
        matchesScalarFilter(filterCategories, row.category) &&
        matchesScalarFilter(filterSections, row.section) &&
        matchesRoles
      );
    });
  }, [
    articles,
    draft,
    filterCategories,
    filterPlanfactArticles,
    filterRoleNames,
    filterSections,
    roles,
    searchText,
  ]);

  const sortedArticles = useMemo(() => {
    const sorted = [...filteredArticles];
    sorted.sort((a, b) => {
      const left = displayValue(a[sortState.key]);
      const right = displayValue(b[sortState.key]);
      return compareValues(left, right, sortState.direction);
    });
    return sorted;
  }, [filteredArticles, sortState]);

  const colCount = 3 + roles.length;
  const tableMinWidth = Math.max(980, 560 + roles.length * 72);

  const handleSort = (key: ArticleAccessSortKey): void => {
    setSortState((current) => toggleSort(current, key));
  };

  const toggleCell = (articleId: number, roleId: number): void => {
    if (!canEdit || saving) return;
    const key = assignmentKey(articleId, roleId);
    setDraft((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleRoleColumn = (roleId: number): void => {
    if (!canEdit || saving) return;
    const articleIds = filteredArticles.map((row) => row.id);
    if (articleIds.length === 0) return;
    const allChecked = articleIds.every((articleId) => Boolean(draft[assignmentKey(articleId, roleId)]));
    const nextValue = !allChecked;
    setDraft((prev) => {
      const next = { ...prev };
      for (const articleId of articleIds) {
        next[assignmentKey(articleId, roleId)] = nextValue;
      }
      return next;
    });
  };

  const handleSave = async (): Promise<void> => {
    if (!canEdit || saving) return;
    setSaving(true);
    setError('');
    const assignments: ArticleRoleAssignment[] = Object.entries(draft)
      .filter(([, checked]) => checked)
      .map(([key]) => {
        const [articleId, roleId] = key.split(':');
        return { article_id: Number(articleId), role_id: Number(roleId) };
      });
    try {
      applyPayload(await saveArticleRolesAccess(assignments));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const colgroup = (
    <colgroup>
      <col className="article-roles-access-page__col article-roles-access-page__col--planfact" />
      <col className="article-roles-access-page__col article-roles-access-page__col--text" />
      <col className="article-roles-access-page__col article-roles-access-page__col--text" />
      {roles.map((role) => (
        <col key={role.id} className="article-roles-access-page__col article-roles-access-page__col--role" />
      ))}
    </colgroup>
  );

  return (
    <div className="data-page articles-page article-roles-access-page">
      {!canView ? (
        <div className="empty-state">Нет доступа к настройке доступов (требуется право ACCESS_ADMIN:VIEW).</div>
      ) : (
        <>
          <div className="page-header-row">
            <h1>Доступы к Статьям Расходов</h1>
            <div className="page-header-row__actions">
              {canEdit ? (
                <button
                  type="button"
                  className="create-button article-roles-access-page__save"
                  onClick={() => void handleSave()}
                  disabled={saving || !isDirty}
                >
                  {saving ? 'Сохранение…' : 'Сохранить'}
                </button>
              ) : null}
              <button
                type="button"
                className={`filters-toggle ${!filtersOpen && hasActiveFilters ? 'filters-toggle--show-active-dot' : ''}`}
                onClick={() => setFiltersOpen((open) => !open)}
                aria-expanded={filtersOpen}
                aria-label={filtersOpen ? 'Скрыть фильтры' : 'Показать фильтры'}
              >
                Фильтры
                <span className="filters-toggle-badge" aria-hidden />
              </button>
            </div>
            <div className="page-header-row__search">
              <input
                type="text"
                className="search-input"
                placeholder="Поиск по текстовым полям"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
          </div>

          {error ? <div className="error-message">{error}</div> : null}

          <div className={`table-layout ${filtersOpen ? '' : 'table-layout--filters-collapsed'}`}>
            {filtersOpen && (
              <aside className="filters-panel">
                <div className="filters-header">
                  <h2>Фильтры</h2>
                  <button type="button" className="link-button" onClick={clearFilters}>
                    Сбросить
                  </button>
                </div>
                <div className="filters-grid">
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Статья из Планфакта"
                      options={planfactArticleFilterOptions}
                      selected={filterPlanfactArticles}
                      onSelectedChange={setFilterPlanfactArticles}
                      formatOption={(v) => v}
                      isOpen={filterMenuOpen === 'planfact_article'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'planfact_article' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Категория"
                      options={categoryFilterOptions}
                      selected={filterCategories}
                      onSelectedChange={setFilterCategories}
                      formatOption={(v) => v}
                      isOpen={filterMenuOpen === 'category'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'category' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Раздел"
                      options={sectionFilterOptions}
                      selected={filterSections}
                      onSelectedChange={setFilterSections}
                      formatOption={(v) => v}
                      isOpen={filterMenuOpen === 'section'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'section' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Роль"
                      options={roleFilterOptions}
                      selected={filterRoleNames}
                      onSelectedChange={setFilterRoleNames}
                      formatOption={(v) => v}
                      isOpen={filterMenuOpen === 'role'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'role' : null)}
                    />
                  </div>
                </div>
              </aside>
            )}

            <section className="table-content">
              {loading ? (
                <div className="loading">Загрузка...</div>
              ) : (
                <div className="table-panel">
                  <div className="table-scroll">
                    <table
                      className="data-table articles-page__table article-roles-access-page__table"
                      style={{ minWidth: tableMinWidth }}
                    >
                      {colgroup}
                      <thead>
                        <tr>
                          <th
                            className="sortable article-roles-access-page__th-planfact"
                            onClick={() => handleSort('planfact_article')}
                            role="button"
                          >
                            Статья из Планфакта
                          </th>
                          <th className="sortable" onClick={() => handleSort('category')} role="button">
                            Категория
                          </th>
                          <th className="sortable" onClick={() => handleSort('section')} role="button">
                            Раздел
                          </th>
                          {roles.map((role) => {
                            const columnCheckedCount = filteredArticles.reduce(
                              (count, row) => count + (draft[assignmentKey(row.id, role.id)] ? 1 : 0),
                              0,
                            );
                            const allFilteredChecked =
                              filteredArticles.length > 0 && columnCheckedCount === filteredArticles.length;
                            const canToggleColumn = canEdit && !saving && filteredArticles.length > 0;
                            return (
                              <th
                                key={role.id}
                                className={`article-roles-access-page__th-role${canToggleColumn ? '' : ' article-roles-access-page__th-role--disabled'}`}
                                title={
                                  canToggleColumn
                                    ? `${role.name}: ${allFilteredChecked ? 'снять все отфильтрованные' : 'выделить все отфильтрованные'}`
                                    : role.name
                                }
                                role={canToggleColumn ? 'button' : undefined}
                                aria-pressed={canToggleColumn ? allFilteredChecked : undefined}
                                onClick={() => toggleRoleColumn(role.id)}
                              >
                                {role.name}
                              </th>
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {sortedArticles.length === 0 ? (
                          <tr>
                            <td colSpan={Math.max(colCount, 1)} className="data-table__empty">
                              Ничего не найдено
                            </td>
                          </tr>
                        ) : (
                          sortedArticles.map((row) => (
                            <tr key={row.id}>
                              <td className="article-roles-access-page__td-planfact">
                                <div className="articles-page__clamp-2">{displayValue(row.planfact_article)}</div>
                              </td>
                              <td>
                                <div className="articles-page__clamp-2">{displayValue(row.category)}</div>
                              </td>
                              <td>
                                <div className="articles-page__clamp-2">{displayValue(row.section)}</div>
                              </td>
                              {roles.map((role) => {
                                const checked = Boolean(draft[assignmentKey(row.id, role.id)]);
                                return (
                                  <td key={role.id} className="article-roles-access-page__td-role">
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      disabled={!canEdit || saving}
                                      aria-label={`${displayValue(row.planfact_article)} — ${role.name}`}
                                      onChange={() => toggleCell(row.id, role.id)}
                                    />
                                  </td>
                                );
                              })}
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="table-panel__footer">
                    <table
                      className="data-table data-table--footer articles-page__table article-roles-access-page__table"
                      style={{ minWidth: tableMinWidth }}
                    >
                      {colgroup}
                      <tfoot>
                        <tr>
                          <td>Итого: {sortedArticles.length}</td>
                          <td />
                          <td />
                          {roles.map((role) => (
                            <td key={role.id} />
                          ))}
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </section>
          </div>
        </>
      )}
    </div>
  );
};

export default ArticleRolesAccessPage;
